import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';
import { toast } from './toast';
import { isNetworkError } from './errorHandling';
import { handleAuthError } from './auth';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 5000;
const REQUEST_TIMEOUT = 10000; // 10 seconds

// Helper functions
const createTimeout = (ms: number) => 
  new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), ms));
const sleep = (ms: number) => 
  new Promise(resolve => setTimeout(resolve, ms));

if (!import.meta.env.VITE_SUPABASE_URL) {
  console.error('Missing VITE_SUPABASE_URL');
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!import.meta.env.VITE_SUPABASE_ANON_KEY) {
  console.error('Missing VITE_SUPABASE_ANON_KEY');
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable');
}

// Create Supabase client with proper configuration
export const supabase = createClient<Database>(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      storage: localStorage,
      detectSessionInUrl: true,
      flowType: 'pkce',
      retryAttempts: MAX_RETRIES,
      retryInterval: INITIAL_RETRY_DELAY,
      storageKey: 'pm-pro-auth-token',
      storageKeyPrefix: 'pm-pro-auth'
    },
    global: {
      headers: {
        'x-client-info': 'pm-pro',
        'x-client-version': '1.0.0',
        'x-client-name': 'pm-pro-web'
      },
      fetch: async (url, options) => {
        let attempt = 0;
        let currentDelay = INITIAL_RETRY_DELAY;

        while (attempt < MAX_RETRIES) {
          try {
            // Add timeout to all requests
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

            const response = await fetch(url, {
              ...options,
              signal: controller.signal
            }).finally(() => clearTimeout(timeoutId));

            if (!response.ok) {
              const data = await response.json().catch(() => ({}));
              
              // Handle session errors
              if (data.code === 'session_not_found' || 
                  data.message?.includes('JWT expired') ||
                  data.message?.includes('Invalid JWT') ||
                  data.message?.includes('Session from session_id claim in JWT does not exist')) {
                await handleAuthError();
                throw new Error('Session expired');
              }

              // Handle auth errors
              if (response.status === 401 || response.status === 403) {
                await handleAuthError();
                throw new Error('Authentication failed');
              }

              if (response.status === 400) {
                if (data.message?.includes('Invalid login credentials')) {
                  throw new Error('Invalid email or password');
                }
                throw new Error(data.message || 'Invalid request');
              }
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            return response;
          } catch (error: any) {
            attempt++;

            // Check if we should retry
            const shouldRetry = attempt < MAX_RETRIES && (
              error.name === 'AbortError' ||
              isNetworkError(error) ||
              error.message.includes('Failed to fetch') ||
              error.message.includes('Network request failed')
            );

            if (!shouldRetry) {
              throw error;
            }

            // Wait before retrying
            await sleep(currentDelay);
            currentDelay = Math.min(currentDelay * 2, MAX_RETRY_DELAY);
          }
        }

        throw new Error('Max retries reached');
      }
    },
    db: {
      schema: 'public'
    },
    realtime: {
      params: {
        eventsPerSecond: 1
      }
    }
  }
);

// Enhanced retry wrapper with timeout
async function withRetry<T>(fn: () => Promise<T>, context: string): Promise<T | null> {
  let attempt = 0;
  let currentDelay = INITIAL_RETRY_DELAY;

  while (attempt < MAX_RETRIES) {
    try {
      // Race between the actual request and a timeout
      const result = await Promise.race([
        fn(),
        createTimeout(REQUEST_TIMEOUT)
      ]);

      if (!result) {
        throw new Error('Empty response');
      }

      return result;
    } catch (error: any) {
      attempt++;

      // Handle specific error types
      if (error.message === 'Request timed out') {
        console.warn(`${context} request timed out (attempt ${attempt}/${MAX_RETRIES})`);
      } else if (error.message?.includes('JWT') || 
                error.message?.includes('auth') || 
                error.message?.includes('session_not_found') ||
                error.message?.includes('Session from session_id claim in JWT does not exist')) {
        await handleAuthError();
        return null;
      }

      // Check if we should retry
      const shouldRetry = attempt < MAX_RETRIES && (
        isNetworkError(error) || 
        error.message === 'Request timed out' ||
        error.message.includes('connection') ||
        error.message.includes('Failed to fetch')
      );

      if (!shouldRetry) {
        console.error(`${context} failed:`, error);
        toast.error(`Failed to ${context.toLowerCase()}. Please try again.`);
        return null;
      }

      // Show retry message and wait
      if (currentDelay > 1000) {
        toast.info(`Retrying ${context.toLowerCase()}...`);
      }

      await sleep(currentDelay);
      currentDelay = Math.min(currentDelay * 2, MAX_RETRY_DELAY);
    }
  }

  toast.error(`Failed to ${context.toLowerCase()} after ${MAX_RETRIES} attempts`);
  return null;
}
